import { useEffect, useState } from 'react';
import { purchaseSubscription } from '../../services/walletService';
import { getGoldPlanPrices, getMySubscription } from '../../services/subscriptionService';
import './GoldSubscriptionModal.scss';

const PLAN_META = [
  { id: '1m', value: 1, label: '1 Mês', key: 'oneMonth', months: 1 },
  { id: '3m', value: 2, label: '3 Meses', key: 'threeMonths', months: 3 },
  { id: '6m', value: 3, label: '6 Meses', key: 'sixMonths', months: 6 },
  { id: '1y', value: 4, label: '1 Ano', key: 'oneYear', months: 12 },
];

const BENEFITS = [
  {
    icon: '✅',
    title: 'Candidaturas Ilimitadas',
    description: 'Não seja restrito a apenas 5 vagas simultâneas. Pegue quantos serviços aguentar!',
  },
  {
    icon: '🛡️',
    title: 'Proteção Anti-Report',
    description: 'Levou uma advertência? Seu primeiro cancelamento/report é "perdoado" e apagado automaticamente.',
  },
  {
    icon: '⚡',
    title: 'Destaque para o Contratante',
    description: 'Ao se aplicar, seu perfil aparece com a tag de prioridade no painel da empresa.',
  },
];

function formatCurrency(value) {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

function formatDate(value) {
  return new Date(value).toLocaleDateString('pt-BR');
}

export default function GoldSubscriptionModal({ onClose, onSubscribe }) {
  const [prices, setPrices] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [mySubscription, setMySubscription] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState('1m');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricesData, subscriptionData] = await Promise.all([
          getGoldPlanPrices(),
          getMySubscription(),
        ]);

        setPrices(pricesData);
        setMySubscription(subscriptionData);
      } catch (err) {
        console.error('Erro ao carregar planos Gold:', err);
        setError('Não foi possível carregar os planos. Tente novamente.');
      } finally {
        setLoadingPrices(false);
      }
    };
    fetchData();
  }, []);

  const plans = prices
    ? PLAN_META.map((meta) => {
        const price = prices[meta.key];
        const fullPrice = prices.oneMonth * meta.months;
        const discount = fullPrice > 0 && meta.months > 1
          ? Math.round((1 - price / fullPrice) * 100)
          : null;

        return { ...meta, price, discount: discount > 0 ? discount : null };
      })
    : [];

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);

  // Só marca um card específico como "Assinado" se o plano ativo for
  // exatamente um dos 4 planos pagos por duração (Gold1Month..Gold12Months).
  // Trial/Plus/Premium não têm card correspondente aqui — pra esses casos
  // mostramos só o banner genérico acima da lista.
  const subscribedPlan = mySubscription?.hasActivePlan
    ? plans.find((p) => p.value === mySubscription.planValue)
    : null;

  // Trava tudo enquanto já existir plano pago ativo — a troca só é liberada
  // quando o contrato atual terminar (regra de negócio pedida). Isso é
  // defesa extra de UX; a checagem que realmente protege é a do backend.
  const isLocked = Boolean(mySubscription?.hasActivePlan);

  const handlePay = async () => {
    if (!selectedPlan || isLocked || loading) return;

    setLoading(true);
    setError(null);

    try {
      await purchaseSubscription(selectedPlan.value);
      alert("Plano ativado com sucesso!");
      onSubscribe?.();
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message ?? "Erro ao processar pagamento";
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gsm-overlay" onClick={onClose}>
      <div className="gsm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gsm-close" onClick={onClose} aria-label="Fechar">×</button>

        <div className="gsm-crown">👑</div>

        <h2 className="gsm-title">Assinatura GOLD</h2>
        <p className="gsm-subtitle">
          Desbloqueie seu potencial máximo e aumente seus ganhos como freelancer de elite na plataforma.
        </p>

        {mySubscription?.hasActivePlan && (
          <div className="gsm-active-banner">
            👑 Você já é Gold — válido até {formatDate(mySubscription.endDate)}.
            A troca de plano fica disponível quando esse contrato terminar.
          </div>
        )}

        <div className="gsm-benefits">
          {BENEFITS.map((b, idx) => (
            <div key={idx} className="gsm-benefit">
              <span className="gsm-benefit-icon">{b.icon}</span>
              <div>
                <p className="gsm-benefit-title">{b.title}</p>
                <p className="gsm-benefit-description">{b.description}</p>
              </div>
            </div>
          ))}
        </div>

        {loadingPrices ? (
          <p className="gsm-loading">Carregando planos...</p>
        ) : (
          <div className="gsm-plans">
            {plans.map((plan) => {
              const isSubscribed = subscribedPlan?.id === plan.id;

              return (
                <button
                  key={plan.id}
                  type="button"
                  className={`gsm-plan ${selectedPlanId === plan.id ? 'gsm-plan--active' : ''} ${isSubscribed ? 'gsm-plan--subscribed' : ''}`}
                  onClick={() => !isLocked && setSelectedPlanId(plan.id)}
                  disabled={isLocked && !isSubscribed}
                >
                  {isSubscribed && (
                    <span className="gsm-plan-subscribed-badge">✓ Assinado</span>
                  )}
                  {!isSubscribed && plan.discount && (
                    <span className="gsm-plan-discount">-{plan.discount}%</span>
                  )}
                  <span className="gsm-plan-label">{plan.label}</span>
                  <span className="gsm-plan-price">R$ {formatCurrency(plan.price)}</span>
                  <span className="gsm-plan-hint">Pagamento único</span>
                </button>
              );
            })}
          </div>
        )}

        {error && <div className="gsm-error">{error}</div>}

        <button
          className="gsm-pay-btn"
          onClick={handlePay}
          disabled={loading || loadingPrices || !selectedPlan || isLocked}
        >
          {isLocked
            ? 'Plano só pode ser trocado após o término do contrato'
            : loading
              ? 'Processando...'
              : `Pagar R$ ${formatCurrency(selectedPlan?.price ?? 0)} com Saldo`}
        </button>
      </div>
    </div>
  );
}